import React, { createContext, useContext, useEffect, useReducer } from 'react';

const AuthContext = createContext();

// Get API URL from environment
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const initialState = {
  user: null,
  token: localStorage.getItem('token'),
  isAuthenticated: !!localStorage.getItem('token'),
  loading: !!localStorage.getItem('token'),
  error: null
};

const authReducer = (state, action) => {
  switch (action.type) {
    case 'USER_LOADED':
      return {
        ...state,
        isAuthenticated: true,
        loading: false,
        user: action.payload,
        error: null
      };
    case 'LOGIN_SUCCESS':
    case 'REGISTER_SUCCESS':
      localStorage.setItem('token', action.payload.token);
      return {
        ...state,
        token: action.payload.token,
        user: action.payload.user,
        isAuthenticated: true,
        loading: false,
        error: null
      };
    case 'AUTH_ERROR':
    case 'LOGIN_FAIL':
    case 'REGISTER_FAIL':
    case 'LOGOUT':
      localStorage.removeItem('token');
      return {
        ...state,
        token: null,
        isAuthenticated: false,
        loading: false,
        user: null,
        error: action.payload
      };
    case 'CLEAR_ERRORS':
      return {
        ...state,
        error: null
      };
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: { ...state.user, ...action.payload },
        error: null
      };
    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Load user (with optional retry handling right after login)
  const loadUser = async ({ retry = 0, logoutOnFail = true } = {}) => {
    const token = localStorage.getItem('token');
    if (!token) {
      if (logoutOnFail) dispatch({ type: 'AUTH_ERROR' });
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (res.ok) {
        const data = await res.json();
        dispatch({
          type: 'USER_LOADED',
          payload: data.user
        });
      } else if (res.status === 401 && !logoutOnFail && retry < 5) {
        // Token might not be recognised immediately after login – retry a few times (exponential back-off)
        const delay = 500 * Math.pow(2, retry); // 0.5s,1s,2s,4s,8s
        setTimeout(() => {
          loadUser({ retry: retry + 1, logoutOnFail });
        }, delay);
      } else {
        if (logoutOnFail) {
          localStorage.removeItem('token');
          dispatch({ type: 'AUTH_ERROR' });
        } else {
          // Give up retries but keep auth state; just mark loading false
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      }
    } catch (err) {
      if (logoutOnFail) {
        localStorage.removeItem('token');
        dispatch({ type: 'AUTH_ERROR' });
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    }
  };

  // Register User
  const register = async (formData) => {
    // Debug: log the payload being sent (password excluded)
    const { password: _p, ...payloadPreview } = formData || {};
    console.log('[AuthContext] Register request payload:', payloadPreview);

    const config = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formData)
    };

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      const res = await fetch(`${API_URL}/api/auth/register/user`, config);
      
      // Debug: log basic response status
      console.log('[AuthContext] Register response status:', res.status, res.statusText);

      const data = await res.json();
      
      // Debug: full response body
      console.log('[AuthContext] Register response body:', data);

      if (res.ok) {
        dispatch({
          type: 'REGISTER_SUCCESS',
          payload: data
        });
        // Populate full profile after token stored (allowing for eventual token propagation)
        loadUser({ logoutOnFail: false });
        return { success: true, message: data.message };
      } else {
        dispatch({
          type: 'REGISTER_FAIL',
          payload: data.message || 'Registration failed'
        });
        return { success: false, message: data.message || 'Registration failed' };
      }
    } catch (err) {
      const message = 'Server error. Please try again.';
      dispatch({
        type: 'REGISTER_FAIL',
        payload: message
      });
      return { success: false, message };
    }
  };

  // Login User
  const login = async (email, password) => {
    console.log('🔐 USER FRONTEND - Login attempt started:', {
      email,
      password,
      passwordLength: password ? password.length : 0,
      userType: 'user',
      apiUrl: `${API_URL}/api/auth/login`,
      timestamp: new Date().toISOString()
    });

    const config = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email, password, userType: 'user' })
    };

    console.log('📤 USER FRONTEND - Sending request:', {
      url: `${API_URL}/api/auth/login`,
      method: 'POST',
      headers: config.headers,
      body: JSON.parse(config.body),
      bodyString: config.body
    });

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      const res = await fetch(`${API_URL}/api/auth/login`, config);
      
      console.log('📥 USER FRONTEND - Response received:', {
        status: res.status,
        statusText: res.statusText,
        ok: res.ok,
        headers: Object.fromEntries(res.headers.entries())
      });

      const data = await res.json();
      
      console.log('📄 USER FRONTEND - Response data:', {
        data,
        success: data.success,
        message: data.message,
        hasToken: !!data.token,
        hasUser: !!data.user
      });

      if (res.ok) {
        console.log('✅ USER FRONTEND - Login successful:', {
          user: data.user,
          tokenLength: data.token ? data.token.length : 0
        });

        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: data
        });
        
        // Fetch full profile now that token is stored (tolerant retry)
        loadUser({ logoutOnFail: false });

        return { success: true, message: data.message };
      } else {
        console.log('❌ USER FRONTEND - Login failed:', {
          status: res.status,
          message: data.message,
          errors: data.errors
        });

        dispatch({
          type: 'LOGIN_FAIL',
          payload: data.message || 'Login failed'
        });
        return { success: false, message: data.message || 'Login failed' };
      }
    } catch (err) {
      console.error('❌ USER FRONTEND - Login error:', {
        error: err.message,
        stack: err.stack,
        name: err.name
      });

      const message = 'Server error. Please try again.';
      dispatch({
        type: 'LOGIN_FAIL',
        payload: message
      });
      return { success: false, message };
    }
  };

  // Logout
  const logout = () => {
    dispatch({ type: 'LOGOUT' });
  };

  // Update user state locally
  const updateUser = (userData) => {
    try {
      console.log('AuthContext: Updating user state with:', userData);
      dispatch({
        type: 'UPDATE_USER',
        payload: userData
      });
    } catch (err) {
      console.error('AuthContext: Error updating user state:', err);
    }
  };

  // Forgot Password
  const forgotPassword = async (email) => {
    const config = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email, userType: 'user' })
    };

    try {
      const res = await fetch(`${API_URL}/api/auth/forgot-password`, config);
      const data = await res.json();

      if (res.ok) {
        return { success: true, message: data.message };
      } else {
        return { success: false, message: data.message || 'Request failed' };
      }
    } catch (err) {
      return { success: false, message: 'Server error. Please try again.' };
    }
  };

  // Reset Password
  const resetPassword = async (token, password) => {
    const config = {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ password, userType: 'user' })
    };

    try {
      const res = await fetch(`${API_URL}/api/auth/reset-password/${token}`, config);
      const data = await res.json();

      if (res.ok) {
        return { success: true, message: data.message };
      } else {
        return { success: false, message: data.message || 'Reset failed' };
      }
    } catch (err) {
      return { success: false, message: 'Server error. Please try again.' };
    }
  };

  // Change Password
  const changePassword = async (currentPassword, newPassword) => {
    const config = {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.token}`
      },
      body: JSON.stringify({ currentPassword, newPassword })
    };

    try {
      const res = await fetch(`${API_URL}/api/users/password`, config);
      const data = await res.json();

      if (res.ok) {
        return { success: true, message: data.message };
      } else {
        return { success: false, message: data.message || 'Password change failed' };
      }
    } catch (err) {
      return { success: false, message: 'Server error. Please try again.' };
    }
  };

  // Clear Errors
  const clearErrors = () => dispatch({ type: 'CLEAR_ERRORS' });

  // Set auth token in headers
  const setAuthToken = (token) => {
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  };

  useEffect(() => {
    loadUser();
  }, []);

  const value = {
    user: state.user,
    token: state.token,
    isAuthenticated: state.isAuthenticated,
    loading: state.loading,
    error: state.error,
    register,
    login,
    logout,
    updateUser,
    forgotPassword,
    resetPassword,
    changePassword,
    clearErrors,
    loadUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 